import { Fragment, useEffect, useRef, useState } from 'react';
import { Timestamp, collection, doc, limit, onSnapshot, orderBy, query, setDoc } from 'firebase/firestore';
import { KeyRound, Building2, ShieldCheck, ScrollText, Download, Plus, Trash2, Copy, Check } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { PageHeader } from '../../components/PageHeader';
import { DateField } from '../../components/DateField';
import { NotificationsToggle } from '../../components/NotificationsToggle';
import { formatPhone, PHONE_MASK } from '../../lib/format';
import { OWNER_ADDRESS, OWNER_EMAIL, OWNER_FIRST } from '../../lib/identity';

const DEFAULT_BIZ = {
  name: 'AJM Aviation Ventures, LLC',
  address: OWNER_ADDRESS,
  email: OWNER_EMAIL,
  phone: '',
};

export function AdminSettings() {
  const { demoMode } = useAuth();
  const [pin, setPin] = useState('AJM2026');
  const [pinDraft, setPinDraft] = useState(pin);
  const [biz, setBiz] = useState(DEFAULT_BIZ);
  const [savingBiz, setSavingBiz] = useState(false);
  // Snapshot of the last-saved business info → drives the Save/Saved button state so
  // the user always knows whether their current edits are persisted.
  const savedBiz = useRef(JSON.stringify(DEFAULT_BIZ));
  const bizDirty = JSON.stringify(biz) !== savedBiz.current;

  // Load the real saved business info (config/global.business) so edits persist.
  useEffect(() => {
    if (demoMode || !db) return;
    const unsub = onSnapshot(doc(db, 'config', 'global'), (snap) => {
      const b = snap.data()?.business;
      if (b) {
        const merged = { ...DEFAULT_BIZ, ...b };
        savedBiz.current = JSON.stringify(merged);
        setBiz(merged);
      }
    });
    return () => unsub();
  }, [demoMode]);

  async function saveBiz() {
    if (demoMode || !db) {
      savedBiz.current = JSON.stringify(biz);
      flash('Business info saved (demo).');
      return;
    }
    setSavingBiz(true);
    try {
      await setDoc(doc(db, 'config', 'global'), { business: biz }, { merge: true });
      savedBiz.current = JSON.stringify(biz);
      flash('Business info saved.');
    } catch {
      flash('Could not save — try again.');
    } finally {
      setSavingBiz(false);
    }
  }
  const [msg, setMsg] = useState<string | null>(null);

  function flash(text: string) {
    setMsg(text);
    setTimeout(() => setMsg(null), 2500);
  }

  return (
    <>
      <PageHeader title="Settings" subtitle="Configuration, announcements, and audit log." />
      {msg && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2 text-sm mb-4">
          {msg}
        </div>
      )}

      <div className="space-y-6">
        <NotificationsToggle />

        <Section icon={<KeyRound size={16} />} title="Registration PIN" desc="Required to start lessee registration. Share via SMS — never email.">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <label className="label">PIN</label>
              <input
                className="input tracking-widest font-mono"
                value={pinDraft}
                onChange={(e) => setPinDraft(e.target.value)}
              />
              <div className="text-xs text-ink-muted mt-1 flex items-center gap-1.5">
                Current: <code>{pin}</code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(pin);
                    flash('PIN copied — paste it into a text.');
                  }}
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-navy-700 hover:bg-slate-100"
                  title="Copy PIN"
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
            </div>
            <button
              onClick={() => {
                setPin(pinDraft);
                flash(demoMode ? 'PIN updated (demo).' : 'PIN updated.');
              }}
              className="btn-primary"
              disabled={pinDraft === pin || !pinDraft.trim()}
            >
              Update PIN
            </button>
          </div>
        </Section>

        <Section icon={<Building2 size={16} />} title="Business info" desc="Shown on statements, lease, and email signatures.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Legal name" value={biz.name} onChange={(v) => setBiz({ ...biz, name: v })} />
            <Field label="Contact email" value={biz.email} onChange={(v) => setBiz({ ...biz, email: v })} />
            <Field label="Contact phone" placeholder={PHONE_MASK} value={biz.phone} onChange={(v) => setBiz({ ...biz, phone: formatPhone(v) })} />
            <Field label="Address" className="sm:col-span-2" value={biz.address} onChange={(v) => setBiz({ ...biz, address: v })} />
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={saveBiz}
              disabled={savingBiz || !bizDirty}
              className={bizDirty ? 'btn-primary' : 'btn-secondary'}
            >
              {savingBiz ? 'Saving…' : bizDirty ? 'Save' : (
                <span className="flex items-center gap-1.5">
                  <Check size={15} /> Saved
                </span>
              )}
            </button>
          </div>
        </Section>

        <Section icon={<ShieldCheck size={16} />} title="Two-factor authentication" desc="Required for admin accounts. Setup via Firebase Authentication.">
          <div className="rounded-lg bg-sky-50 border border-sky-200 px-4 py-3 text-sm text-navy-900">
            2FA setup screen ships when Firebase is wired. The flow uses Firebase Auth’s
            built-in multi-factor support (phone-based by default). Lessees see an optional setup
            prompt after first login.
          </div>
        </Section>

        <Section icon={<ScrollText size={16} />} title="Audit log" desc="Who did what, and when — every registration, booking, document, flight, charge, squawk and settings change. Written by the server; nothing here can be edited or removed.">
          <AuditLogPanel demoMode={demoMode} />
        </Section>

        <Section icon={<Download size={16} />} title="Database backup" desc="Download a JSON snapshot of every collection.">
          <button
            onClick={() => {
              const blob = new Blob([JSON.stringify({ exported: 'demo', notice: 'Live backups require Firebase admin SDK' }, null, 2)], {
                type: 'application/json',
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `ajm-backup-${new Date().toISOString().slice(0, 10)}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="btn-secondary"
          >
            Download backup (demo stub)
          </button>
        </Section>
      </div>
    </>
  );
}

function Section({
  icon,
  title,
  desc,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  desc?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card p-5 sm:p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="mt-0.5 text-navy-700">{icon}</div>
        <div>
          <h2 className="font-display text-lg font-semibold text-navy-900">{title}</h2>
          {desc && <p className="text-sm text-ink-muted mt-0.5">{desc}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  className = '',
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  className?: string;
  placeholder?: string;
}) {
  return (
    <div className={className}>
      <label className="label">{label}</label>
      <input className="input" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

// ───────────────────────────── audit log ─────────────────────────────
// Reads the server-written auditLog collection (functions/index.js auditWriter;
// history before 10/22/2026 reconstructed by scripts/backfill-audit-log.mjs).
// Clients can't write it, so what's shown is what happened.
interface AuditEntry {
  id: string;
  at: Timestamp | null;
  collection: string;
  docId: string;
  op: 'create' | 'update' | 'delete';
  action: string;
  entity: string;
  actorName: string;
  actorRole?: 'admin' | 'cfi' | 'pilot' | null;
  actorType: string;
  changed?: string[];
  diff?: Record<string, { from?: unknown; to?: unknown }>;
  source: 'live' | 'backfill';
}

const ago = (minutes: number) => Timestamp.fromDate(new Date(Date.now() - minutes * 60_000));
const DEMO_AUDIT: AuditEntry[] = [
  { id: 'a1', at: ago(12), collection: 'documents', docId: 'd-004', op: 'update', action: 'Verified document', entity: `Medical · Morgan Reyes`, actorName: OWNER_FIRST, actorRole: 'admin', actorType: 'user', changed: ['verificationStatus', 'verifiedAt'], diff: { verificationStatus: { from: 'pending', to: 'verified' } }, source: 'live' },
  { id: 'a2', at: ago(13), collection: 'bookings', docId: 'b-001', op: 'update', action: 'Approved booking', entity: 'Brian Matthews · Oct 24, 9:00 AM', actorName: OWNER_FIRST, actorRole: 'admin', actorType: 'user', changed: ['status', 'approvedAt'], diff: { status: { from: 'pending_approval', to: 'approved' } }, source: 'live' },
  { id: 'a3', at: ago(60 * 17), collection: 'transactions', docId: 'tx_demo_001', op: 'create', action: 'Card charged', entity: '$130 · Brian Matthews · Flight time — 1.3 Hobbs hr', actorName: 'System', actorRole: null, actorType: 'system', changed: [], diff: {}, source: 'live' },
  { id: 'a4', at: ago(60 * 17 + 4), collection: 'flights', docId: 'f-001', op: 'create', action: 'Logged flight', entity: 'Brian Matthews · 2026-10-21 · KLGB → KSNA · 1.3 hr', actorName: 'Brian Matthews', actorRole: 'pilot', actorType: 'user', changed: [], diff: {}, source: 'live' },
  { id: 'a5', at: ago(60 * 49), collection: 'users', docId: 'u-jordan', op: 'update', action: 'Approved registration', entity: 'Jordan Lee', actorName: OWNER_FIRST, actorRole: 'admin', actorType: 'user', changed: ['status', 'approvedAt'], diff: { status: { from: 'pending_approval', to: 'profile_incomplete' } }, source: 'live' },
];

const COLLECTION_LABEL: Record<string, string> = {
  users: 'Pilot',
  bookings: 'Booking',
  documents: 'Document',
  flights: 'Flight',
  transactions: 'Charge',
  squawks: 'Squawk',
  checkouts: 'Checkout',
  flightApprovals: 'Student flight',
  leaseSignatures: 'Lease',
  aircraft: 'Aircraft',
  maintenance: 'Maintenance',
  aircraftLogbook: 'Logbook',
  announcements: 'Announcement',
  config: 'Settings',
  discoveryRequests: 'Discovery',
  discoveryWaivers: 'Discovery',
  readingDiscrepancies: 'Reading check',
  billingCycles: 'Billing cycle',
};
const ROLE_TAG: Record<string, string> = { admin: 'admin', cfi: 'CFI', pilot: '', system: 'server', public: 'public' };

function whenLabel(at: Timestamp | null): string {
  if (!at) return '—';
  const d = at.toDate();
  const now = new Date();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(d, now)) return `Today ${time}`;
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  if (sameDay(d, y)) return `Yesterday ${time}`;
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
  return `${date}, ${time}`;
}

const PAGE = 50;

function AuditLogPanel({ demoMode }: { demoMode: boolean }) {
  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [cap, setCap] = useState(PAGE);
  const [needle, setNeedle] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    if (demoMode || !db) {
      setRows(DEMO_AUDIT);
      setLoaded(true);
      return;
    }
    const unsub = onSnapshot(
      query(collection(db, 'auditLog'), orderBy('at', 'desc'), limit(cap)),
      (snap) => {
        setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<AuditEntry, 'id'>) })));
        setLoaded(true);
        setFailed(false);
      },
      () => {
        setLoaded(true);
        setFailed(true);
      }
    );
    return () => unsub();
  }, [demoMode, cap]);

  const q = needle.trim().toLowerCase();
  const shown = q
    ? rows.filter((r) => [r.actorName, r.action, r.entity, COLLECTION_LABEL[r.collection] ?? r.collection, r.docId].join(' ').toLowerCase().includes(q))
    : rows;
  const oldestLive = rows.find((r) => r.source === 'live');
  const hasBackfill = rows.some((r) => r.source === 'backfill');

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
        <input
          className="input sm:max-w-xs"
          placeholder="Search who, what or which record"
          value={needle}
          onChange={(e) => setNeedle(e.target.value)}
          aria-label="Search the audit log"
        />
        <div className="text-xs text-ink-muted sm:ml-auto">
          {loaded ? `${shown.length} of the latest ${rows.length}` : 'Loading…'}
        </div>
      </div>

      {failed && (
        <p className="text-sm text-red-700 mb-3">Couldn’t read the audit log — this view is admin-only and needs a live connection.</p>
      )}
      {loaded && !failed && shown.length === 0 && (
        <p className="text-sm text-ink-muted py-4">{q ? 'Nothing matches that search.' : 'Nothing recorded yet.'}</p>
      )}

      {shown.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-ink-muted text-xs uppercase tracking-wider">
            <tr>
              <th className="py-2 pr-3 font-medium whitespace-nowrap">When</th>
              <th className="py-2 pr-3 font-medium">Who</th>
              <th className="py-2 pr-3 font-medium">What</th>
              <th className="py-2 font-medium hidden md:table-cell">Record</th>
            </tr>
          </thead>
          <tbody className="text-ink">
            {shown.map((r) => {
              const tag = r.actorRole ? ROLE_TAG[r.actorRole] : ROLE_TAG[r.actorType] ?? '';
              const isOpen = open === r.id;
              const diffKeys = Object.keys(r.diff ?? {});
              return (
                <Fragment key={r.id}>
                  <tr
                    className={`border-b border-slate-100 last:border-none cursor-pointer hover:bg-slate-50 ${isOpen ? 'bg-slate-50' : ''}`}
                    onClick={() => setOpen(isOpen ? null : r.id)}
                    title={r.at ? r.at.toDate().toLocaleString() : undefined}
                  >
                    <td className="py-2 pr-3 whitespace-nowrap text-ink-muted align-top">{whenLabel(r.at)}</td>
                    <td className="py-2 pr-3 align-top sm:whitespace-nowrap">
                      {r.actorName}
                      {tag && <span className="ml-1.5 text-[11px] uppercase tracking-wider text-ink-muted">{tag}</span>}
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <div>{r.action}</div>
                      <div className="md:hidden text-xs text-ink-muted mt-0.5">{r.entity}</div>
                    </td>
                    <td className="py-2 align-top hidden md:table-cell">
                      <span className="text-[11px] uppercase tracking-wider text-ink-muted mr-1.5">{COLLECTION_LABEL[r.collection] ?? r.collection}</span>
                      {r.entity}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-slate-100 last:border-none bg-slate-50">
                      <td colSpan={4} className="pb-3 pt-0 px-1 text-xs text-ink-muted">
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                          <span>
                            {COLLECTION_LABEL[r.collection] ?? r.collection} <span className="font-mono">{r.collection}/{r.docId}</span>
                          </span>
                          <span>{r.op}</span>
                          {r.at && <span>{r.at.toDate().toLocaleString()}</span>}
                          {r.source === 'backfill' && <span>reconstructed from the record’s own timestamps</span>}
                        </div>
                        {diffKeys.length > 0 && (
                          <div className="mt-1.5 grid gap-y-0.5" style={{ gridTemplateColumns: 'max-content 1fr' }}>
                            {diffKeys.map((k) => {
                              const v = r.diff![k];
                              return (
                                <Fragment key={k}>
                                  <span className="font-mono pr-3">{k}</span>
                                  <span>
                                    {v.from !== undefined && <span className="line-through opacity-70 mr-1.5">{String(v.from)}</span>}
                                    {v.to !== undefined ? <span className="text-ink">{String(v.to)}</span> : <span>removed</span>}
                                  </span>
                                </Fragment>
                              );
                            })}
                          </div>
                        )}
                        {diffKeys.length === 0 && r.changed && r.changed.length > 0 && r.op !== 'create' && (
                          <div className="mt-1">Changed: {r.changed.join(', ')}</div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-ink-muted">
        {!demoMode && rows.length >= cap && (
          <button type="button" className="text-navy-700 font-medium hover:underline" onClick={() => setCap((c) => c + 200)}>
            Show more
          </button>
        )}
        {hasBackfill && (
          <span>
            Lines before {oldestLive?.at ? oldestLive.at.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Oct 22, 2026'} were rebuilt from each record’s own timestamps; since then every line is written by the server as it happens.
          </span>
        )}
      </div>
    </div>
  );
}
